import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { VAT_CONDITIONS, VAT_CONDITION_LABELS, can, type VatCondition } from "@devengo/shared";

import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  PageTitle,
  Select,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import { formatCuit } from "../lib/format";
import { useCompanies } from "../lib/hooks";
import { useCurrentUser } from "../lib/session";

interface Counterpart {
  id: string;
  docType: number;
  docNumber: string;
  name: string;
  vatCondition: VatCondition | null;
  status: "active" | "pending_review";
  isClient: boolean;
  isSupplier: boolean;
}

type Tipo = "todos" | "cliente" | "proveedor";
const TABS: { value: Tipo; label: string }[] = [
  { value: "todos", label: "Todas" },
  { value: "cliente", label: "Clientes" },
  { value: "proveedor", label: "Proveedores" },
];

const DOC = { 80: "CUIT", 86: "CUIL", 96: "DNI", 99: "C. final" } as Record<number, string>;

/**
 * Clients and suppliers. One record per document for the whole studio (name and IVA condition are
 * kept once); choosing a company shows that company's own clients and suppliers.
 */
export function CounterpartsPage() {
  const user = useCurrentUser();
  const companies = useCompanies();
  const [query, setQuery] = useState("");
  const [tipo, setTipo] = useState<Tipo>("todos");
  const [companyId, setCompanyId] = useState("");
  const [adding, setAdding] = useState(false);
  const canEdit = can(user.role, "manageCounterparts");
  const list = useQuery({
    queryKey: ["counterparts", companyId, query, tipo],
    queryFn: () => {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (tipo !== "todos") params.set("tipo", tipo);
      const qs = params.toString();
      const base = companyId ? `/api/companies/${companyId}/counterparts` : "/api/counterparts";
      return api.get<Counterpart[]>(`${base}${qs ? `?${qs}` : ""}`);
    },
  });
  const pending = list.data?.filter((c) => c.status === "pending_review").length ?? 0;
  const company = companies.data?.find((c) => c.id === companyId);

  return (
    <>
      <PageTitle
        subtitle={
          company
            ? `Clientes y proveedores de ${company.legalName}. El nombre y la condición frente al IVA son los mismos para todas las empresas.`
            : "Clientes y proveedores del estudio: un documento, un registro, compartido por todas las empresas."
        }
        actions={
          <>
            <div className="w-72">
              <Select
                aria-label="Empresa"
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setAdding(false);
                }}
              >
                <option value="">Todas las empresas</option>
                {companies.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.legalName}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-80">
              <Input
                placeholder="Buscar por nombre o CUIT"
                aria-label="Buscar contraparte"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {canEdit && companyId && !adding ? (
              <Button onClick={() => setAdding(true)}>Agregar a la empresa</Button>
            ) : null}
          </>
        }
      >
        Contrapartes
      </PageTitle>
      {adding && companyId ? (
        <AddCounterpart companyId={companyId} onDone={() => setAdding(false)} />
      ) : null}
      {pending ? (
        <p className="mb-2 text-sm text-amber-800">
          {pending} contraparte{pending === 1 ? "" : "s"} creada{pending === 1 ? "" : "s"} por una
          importación: revisá el nombre y la condición frente al IVA y confirmalas.
        </p>
      ) : null}
      <ErrorBox error={list.error} />
      <div className="mb-3 flex gap-1" role="tablist" aria-label="Tipo de contraparte">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tipo === t.value}
            className={`rounded-md px-3 py-1.5 text-sm ${
              tipo === t.value
                ? "bg-brand-primary text-white"
                : "border border-brand-line bg-white hover:bg-brand-tint"
            }`}
            onClick={() => setTipo(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <Card>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={thClass}>Nombre</th>
              <th className={thClass}>Documento</th>
              <th className={thClass}>Tipo</th>
              <th className={thClass}>Condición IVA</th>
              <th className={thClass}>Estado</th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody>
            {list.data?.map((c) => (
              <Row key={c.id} counterpart={c} canEdit={canEdit} />
            ))}
            {list.data?.length === 0 ? (
              <tr>
                <td className={`${tdClass} text-brand-muted`} colSpan={6}>
                  {company
                    ? "Esta empresa todavía no tiene contrapartes así. Se agregan solas al cargar comprobantes, o con «Agregar a la empresa»."
                    : "No hay contrapartes que coincidan."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function Row({ counterpart: c, canEdit }: { counterpart: Counterpart; canEdit: boolean }) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const [vat, setVat] = useState<string>(c.vatCondition ?? "");
  const save = useMutation({
    mutationFn: (body: object) => api.patch(`/api/counterparts/${c.id}`, body),
    onSuccess: async () => {
      setEditing(false);
      await client.invalidateQueries({ queryKey: ["counterparts"] });
    },
  });
  if (editing) {
    return (
      <tr>
        <td className={tdClass}>
          <Input value={name} onChange={(e) => setName(e.target.value)} aria-label="Nombre" />
        </td>
        <td className={`${tdClass} tabular`}>
          {DOC[c.docType]} {c.docType === 99 ? "" : formatCuit(c.docNumber)}
        </td>
        <td className={tdClass} />
        <td className={tdClass}>
          <Select value={vat} onChange={(e) => setVat(e.target.value)} aria-label="Condición IVA">
            <option value="">Sin dato</option>
            {VAT_CONDITIONS.map((v) => (
              <option key={v} value={v}>
                {VAT_CONDITION_LABELS[v]}
              </option>
            ))}
          </Select>
        </td>
        <td className={tdClass} colSpan={2}>
          <div className="flex gap-2">
            <Button
              disabled={save.isPending || !name.trim()}
              onClick={() =>
                save.mutate({
                  name: name.trim(),
                  vatCondition: vat === "" ? null : vat,
                  ...(c.status === "pending_review" ? { status: "active" } : {}),
                })
              }
            >
              Guardar
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
          </div>
          <ErrorBox error={save.error} />
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className={tdClass}>{c.name}</td>
      <td className={`${tdClass} tabular`}>
        {DOC[c.docType]} {c.docType === 99 ? "" : formatCuit(c.docNumber)}
      </td>
      <td className={tdClass}>
        <span className="flex flex-wrap gap-1">
          {c.isClient ? <Badge tone="brand">Cliente</Badge> : null}
          {c.isSupplier ? <Badge tone="neutral">Proveedor</Badge> : null}
          {!c.isClient && !c.isSupplier ? "—" : null}
        </span>
      </td>
      <td className={tdClass}>{c.vatCondition ? VAT_CONDITION_LABELS[c.vatCondition] : "—"}</td>
      <td className={tdClass}>
        {c.status === "pending_review" ? (
          <Badge tone="warning">Revisar</Badge>
        ) : (
          <Badge tone="success">Activa</Badge>
        )}
      </td>
      <td className={`${tdClass} text-right`}>
        {canEdit ? (
          <Button
            variant="ghost"
            onClick={() => {
              // Always start from what is stored: a cancelled edit leaves nothing behind.
              setName(c.name);
              setVat(c.vatCondition ?? "");
              setEditing(true);
            }}
          >
            {c.status === "pending_review" ? "Revisar" : "Editar"}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

function AddCounterpart({ companyId, onDone }: { companyId: string; onDone: () => void }) {
  const client = useQueryClient();
  const [role, setRole] = useState<"cliente" | "proveedor">("proveedor");
  const [docType, setDocType] = useState("80");
  const [docNumber, setDocNumber] = useState("");
  const [name, setName] = useState("");
  const [vat, setVat] = useState("");
  const add = useMutation({
    mutationFn: () =>
      api.post(`/api/companies/${companyId}/counterparts`, {
        role,
        docType: Number(docType),
        docNumber,
        name,
        vatCondition: vat === "" ? null : vat,
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["counterparts"] });
      await client.invalidateQueries({ queryKey: ["company-counterparts", companyId] });
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  return (
    <Card title="Agregar cliente o proveedor a la empresa" className="mb-4">
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <Field label="Es">
          <Select value={role} onChange={(e) => setRole(e.target.value as "cliente" | "proveedor")}>
            <option value="proveedor">Proveedor</option>
            <option value="cliente">Cliente</option>
          </Select>
        </Field>
        <Field label="Documento">
          <Select value={docType} onChange={(e) => setDocType(e.target.value)}>
            <option value="80">CUIT</option>
            <option value="86">CUIL</option>
            <option value="96">DNI</option>
          </Select>
        </Field>
        <Field label="Número">
          <Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} required />
        </Field>
        <div className="md:col-span-2">
          <Field label="Nombre o razón social" hint="Si el documento ya existe, se usa su ficha.">
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
        </div>
        <Field label="Condición IVA">
          <Select value={vat} onChange={(e) => setVat(e.target.value)}>
            <option value="">Sin dato</option>
            {VAT_CONDITIONS.map((v) => (
              <option key={v} value={v}>
                {VAT_CONDITION_LABELS[v]}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex gap-2 md:col-span-6">
          <Button type="submit" disabled={add.isPending}>
            Agregar
          </Button>
          <Button variant="secondary" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
      <ErrorBox error={add.error} />
    </Card>
  );
}
